import { alpha } from '@mui/material/styles';

// UI-005A1 semantic UI tokens.
//
// Presentation only. This module must never make routing, role, auth, data, or
// domain decisions — it maps a theme onto named presentation roles and nothing else.
//
// `getTokens(theme)` returns a COMPLETE token structure even when `theme.custom`
// is absent, because many existing tests render shared components under the bare
// MUI default theme rather than the OutreachOS application theme.

// Explicit CSS lengths, never numeric MUI `shape.borderRadius` multipliers.
export const UI_RADIUS = Object.freeze({
  control: '12px',
  tile: '16px',
  card: '18px',
  panel: '24px',
  pill: '999px',
});

const FALLBACK_INK = { light: '#163126', dark: '#F3EBDD' };

const FALLBACK_SURFACES = {
  light: {
    page: '#f1ede3',
    shell: '#f7f1e6',
    card: '#fcf9f2',
    cardAlt: '#f3ecde',
    inset: '#f3ecde',
    field: '#f4edde',
    raised: '#ece4d4',
  },
  dark: {
    page: '#111111',
    shell: '#181818',
    card: '#202020',
    cardAlt: '#262626',
    inset: '#262626',
    field: '#2b2b2b',
    raised: '#313131',
  },
};

const FALLBACK_SHADOWS = {
  light: {
    none: 'none',
    soft: '0 10px 24px rgba(41, 34, 21, 0.08)',
    card: '0 18px 40px rgba(46, 38, 23, 0.1)',
    raised: '0 24px 52px rgba(46, 38, 23, 0.14)',
    overlay: '0 30px 64px rgba(46, 38, 23, 0.18)',
  },
  dark: {
    none: 'none',
    soft: '0 10px 28px rgba(0, 0, 0, 0.22)',
    card: '0 20px 46px rgba(0, 0, 0, 0.28)',
    raised: '0 28px 60px rgba(0, 0, 0, 0.36)',
    overlay: '0 32px 72px rgba(0, 0, 0, 0.46)',
  },
};

// Categorical series validated for OKLCH lightness band, chroma floor,
// protan/deutan separation, and >= 3:1 contrast against each mode's card surface.
const FALLBACK_CHART_SERIES = {
  light: ['#1D6F42', '#B07A00', '#2F5DA8', '#B3261E', '#00908C', '#6B4E9E'],
  dark: ['#4FA26E', '#C08322', '#6E8FD0', '#D9645A', '#12A5A0', '#9A7BD0'],
};

// Foregrounds legible against that mode's normal card surface.
const FALLBACK_STATUS = {
  light: {
    positive: '#4E7A16',
    caution: '#7A5300',
    critical: '#B3261E',
    info: '#436092',
    neutral: '#556257',
  },
  dark: {
    positive: '#7AA529',
    caution: '#F2AE00',
    critical: '#E9827A',
    info: '#8FA9D6',
    neutral: '#B5AEA2',
  },
};

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

// Tolerant alpha(): a consumer theme may carry a color value MUI cannot parse.
function withAlpha(color, opacity, fallbackColor) {
  try {
    return alpha(color, opacity);
  } catch {
    return fallbackColor;
  }
}

function statusRole(mode, foreground, tintSource) {
  const isDark = mode === 'dark';
  return {
    foreground,
    main: tintSource,
    surface: withAlpha(tintSource, isDark ? 0.16 : 0.1, 'transparent'),
    border: withAlpha(tintSource, isDark ? 0.4 : 0.3, foreground),
  };
}

/**
 * Mode-aware default token structure. Reads whatever the supplied theme exposes
 * and falls back to the OutreachOS defaults when it exposes nothing.
 *
 * `surfaceOverrides` lets the application theme pass its own surface ramp in so
 * the token surfaces stay in lockstep with it instead of drifting from a copy.
 */
export function buildUiTokens(mode, palette, surfaceOverrides) {
  const isDark = mode === 'dark';
  const key = isDark ? 'dark' : 'light';
  const ink = palette?.text?.primary || FALLBACK_INK[key];
  const surfaces = { ...FALLBACK_SURFACES[key], ...surfaceOverrides };
  const status = FALLBACK_STATUS[key];

  const card = palette?.background?.paper || surfaces.card;
  const page = palette?.background?.default || surfaces.page;
  const primaryMain = palette?.primary?.main || (isDark ? '#5FB07C' : '#1D6F42');

  return {
    surface: {
      page,
      shell: surfaces.shell,
      card,
      cardAlt: surfaces.cardAlt,
      inset: surfaces.inset,
      field: surfaces.field,
      raised: surfaces.raised,
    },
    border: {
      subtle: withAlpha(ink, isDark ? 0.08 : 0.06, 'transparent'),
      default: palette?.divider || withAlpha(ink, isDark ? 0.14 : 0.1, 'transparent'),
      strong: withAlpha(ink, isDark ? 0.26 : 0.22, ink),
    },
    radius: { ...UI_RADIUS },
    shadow: { ...FALLBACK_SHADOWS[key] },
    status: {
      positive: statusRole(mode, status.positive, palette?.success?.main || status.positive),
      caution: statusRole(mode, status.caution, palette?.warning?.main || status.caution),
      critical: statusRole(mode, status.critical, palette?.error?.main || status.critical),
      info: statusRole(mode, status.info, palette?.info?.main || status.info),
      neutral: statusRole(mode, status.neutral, status.neutral),
    },
    focus: {
      color: primaryMain,
      // Ready-to-use `outline` shorthand. Consumers restoring the keyboard focus
      // ring should prefer this over composing it from `color`, because an
      // outline survives component-level `boxShadow` overrides that a focus ring
      // built from `ring` would silently lose to.
      outline: `2px solid ${primaryMain}`,
      // Soft halo, for surfaces that want a box-shadow focus treatment instead.
      ring: `0 0 0 3px ${withAlpha(primaryMain, isDark ? 0.42 : 0.32, 'transparent')}`,
      offset: '2px',
    },
    chart: {
      grid: withAlpha(ink, isDark ? 0.12 : 0.1, 'transparent'),
      axis: palette?.text?.secondary || (isDark ? '#B5AEA2' : '#556257'),
      tooltipSurface: surfaces.cardAlt,
      tooltipBorder: withAlpha(ink, isDark ? 0.16 : 0.12, 'transparent'),
      cursor: withAlpha(primaryMain, isDark ? 0.18 : 0.12, 'transparent'),
      categorical: [...FALLBACK_CHART_SERIES[key]],
    },
  };
}

// Arrays replace wholesale; plain objects merge key-by-key.
function mergeTokens(base, override) {
  if (!isPlainObject(override)) return base;

  const merged = { ...base };
  Object.keys(override).forEach((tokenKey) => {
    const next = override[tokenKey];
    if (next === undefined) return;
    merged[tokenKey] = isPlainObject(next) && isPlainObject(base[tokenKey])
      ? mergeTokens(base[tokenKey], next)
      : next;
  });
  return merged;
}

/**
 * Semantic token accessor for shared presentation components.
 *
 * Prefers `theme.custom.ui` when the OutreachOS theme is mounted, and otherwise
 * synthesizes an equivalent structure from whatever palette the theme exposes.
 */
export function getTokens(theme) {
  const mode = theme?.palette?.mode === 'dark' ? 'dark' : 'light';
  return mergeTokens(buildUiTokens(mode, theme?.palette), theme?.custom?.ui);
}

export default getTokens;
